import React, { useMemo } from 'react';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';

const ChartInner = ({ loading, error, isEmpty, hasData, children }) => {
    if (loading) return (
        <div className="chart-empty">
            <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
            <p>Loading...</p>
        </div>
    );
    if (error) return (
        <div className="chart-empty chart-empty--error">
            <i className="pi pi-exclamation-circle" />
            <p>Failed to load data</p>
        </div>
    );
    if (!hasData) return (
        <div className="chart-empty">
            <i className="pi pi-chart-bar" />
            <p>Search the DC to load data</p>
        </div>
    );
    if (isEmpty) return (
        <div className="chart-empty">
            <i className="pi pi-info-circle" />
            <p>No data found</p>
        </div>
    );
    return children;
};

const ServiceReachabilityChart = ({ deviceReachability, loading, error }) => {
    const today     = deviceReachability?.today;
    const yesterday = deviceReachability?.yesterday;

    const { data, options } = useMemo(() => ({
        data: {
            labels: ['Today', 'Yesterday'],
            datasets: [
                {
                    label: 'Reachable',
                    data: [
                        today?.reachablePercentage != null ? today.reachablePercentage : null,
                        yesterday?.reachablePercentage != null ? yesterday.reachablePercentage : null
                    ],
                    counts: [today?.reachable ?? 0, yesterday?.reachable ?? 0],
                    backgroundColor: '#90CAF9',
                    borderRadius: 4,
                    barPercentage: 0.35,
                    categoryPercentage: 0.6
                },
                {
                    label: 'Unreachable',
                    data: [
                        today?.reachablePercentage != null ? parseFloat((100 - today.reachablePercentage).toFixed(2)) : null,
                        yesterday?.reachablePercentage != null ? parseFloat((100 - yesterday.reachablePercentage).toFixed(2)) : null
                    ],
                    counts: [today?.unreachable ?? 0, yesterday?.unreachable ?? 0],
                    backgroundColor: '#CFD8DC',
                    borderRadius: 4,
                    barPercentage: 0.35,
                    categoryPercentage: 0.6
                }
            ]
        },
        options: {
            plugins: {
                legend: { position: 'bottom', labels: { boxWidth: 12 } },
                tooltip: {
                    callbacks: {
                        label: (ctx) => {
                            const count = ctx.dataset.counts?.[ctx.dataIndex] ?? 0;
                            const pct   = typeof ctx.raw === 'number' ? ctx.raw.toFixed(2) : 0;
                            return ` ${ctx.dataset.label}: ${count} devices (${pct}%)`;
                        }
                    }
                }
            },
            scales: {
                x: { stacked: true, grid: { display: false } },
                y: {
                    stacked: true,
                    grid: { display: false },
                    min: 0, max: 100,
                    ticks: { callback: (val) => `${val}%` }
                }
            },
            responsive: true,
            maintainAspectRatio: false
        }
    }), [deviceReachability]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="chart-card">
            <h5 className="chart-title">Service Reachability</h5>
            <ChartInner
                loading={loading}
                error={error}
                hasData={!!deviceReachability}
                isEmpty={(!today && !yesterday) || (today?.totalDevices === 0 && yesterday?.totalDevices === 0)}
            >
                <div className="chart-wrapper">
                    <Chart type="bar" data={data} options={options} data-testid="chart" />
                </div>
            </ChartInner>
        </div>
    );
};

export default ServiceReachabilityChart;
